import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import FormHeader from 'components/global/FormHeader';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import { ENUMS, REPORT } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';
import PATHS, { mergePaths } from 'utils/paths';

import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author Burak Ak
 */

export const TimeStatsReportConfig = {
	id: DYNAMICLISTS.TIME_STATS_REPORT,
	columns: [
		{ Header: 'id', accessor: 'ID', filterType: FilterType.number, width: 70, show: false, showCopyButton: true },
		{
			Header: 'orderID',
			accessor: 'orderID',
			filterType: FilterType.string,
			width: 110,
			showOnMobile: true,
			showCopyButton: true
		},

		{
			Header: 'newOrderDate',
			accessor: 'newOrderDate',
			filterType: FilterType.date,
			renderCell: ListComponents.DATETIME,
			width: 110,
			filterRemovable: false,
			customFilterOptions: [FilterAction.between, FilterAction.on]
		},
		{
			Header: 'pickupLocation',
			accessor: 'pickupLocation',
			filterType: FilterType.enumMulti,
			enumName: ENUMS.PICKUP_LOCATION,
			width: 100,
			show: false
		},
		{
			Header: 'deliveryCompany',
			accessor: 'deliveryName',
			filterType: FilterType.enumMulti,
			width: 90,
			enumName: ENUMS.DELIVERY_COMPANIES_USED,
			renderCell: ListComponents.DC_LOGO
		},
		{
			Header: 'shipmentNumber',
			accessor: 'shipmentNumber',
			filterType: FilterType.string,
			width: 100,
			show: false,
			renderCell: ListComponents.TRACKING_URL
		},
		{ Header: 'city', accessor: 'cityName', filterType: FilterType.multiSelection, width: 100, show: false },
		{
			Header: 'country',
			accessor: 'countryName',
			filterType: FilterType.enumMulti,
			enumName: ENUMS.COUNTRIES,
			filterPrefix: 'country.',
			renderCell: ListComponents.COUNTRY,
			width: 100,
			show: false
		},

		{
			Header: 'paymentConfirmTime',
			accessor: 'paymentConfirmTime',
			filterType: FilterType.date,
			width: 110,
			renderCell: ListComponents.DATETIME
		},
		{
			Header: 'pickupCutoffTime',
			accessor: 'pickupCutoffTime',
			filterType: FilterType.date,
			width: 110,
			renderCell: ListComponents.DATETIME
		},
		{
			Header: 'pickupDate',
			accessor: 'pickupDate',
			filterType: FilterType.date,
			width: 110,
			renderCell: ListComponents.DATETIME
		},
		{
			Header: 'undeliveredAttemptDate',
			accessor: 'undeliveredAttemptDate',
			filterType: FilterType.date,
			width: 110,
			renderCell: ListComponents.DATETIME
		},
		{
			Header: 'deliveryTime',
			accessor: 'deliveryTime',
			filterType: FilterType.string,
			width: 110
		},
		{
			Header: 'returnDate',
			accessor: 'returnDate',
			filterType: FilterType.date,
			width: 110,
			renderCell: ListComponents.DATETIME
		},
		{
			Header: 'returnCreateDate',
			accessor: 'returnCreateDate',
			filterType: FilterType.date,
			width: 110,
			renderCell: ListComponents.DATETIME
		},
		{
			Header: 'confirmReturnDate',
			accessor: 'confirmReturnDate',
			filterType: FilterType.date,
			width: 110,
			show: false,
			renderCell: ListComponents.DATETIME
		},
		{
			Header: 'canceledDate',
			accessor: 'canceledDate',
			filterType: FilterType.date,
			width: 110,
			renderCell: ListComponents.DATETIME
		},
		{
			Header: 'cancelReason',
			accessor: 'cancelReason',
			filterType: FilterType.string,
			width: 110,
			show: false
		},
		{
			Header: 'undeliveredAttemptCnt',
			accessor: 'undeliveredAttemptCnt',
			filterType: FilterType.number,
			width: 80
		},

		{
			Header: 'deliveryDate',
			accessor: 'deliveryDate',
			filterType: FilterType.date,
			width: 110,
			renderCell: ListComponents.DATETIME
		},
		{
			Header: 'shipmentCanceledDate',
			accessor: 'shipmentCanceledDate',
			filterType: FilterType.date,
			width: 110,
			renderCell: ListComponents.DATETIME
		},
		{
			Header: 'shipmentCreatedDate',
			accessor: 'shipmentCreatedDate',
			filterType: FilterType.date,

			renderCell: ListComponents.DATETIME
		},

		{
			Header: 'status',
			accessor: 'status',
			renderCell: ListComponents.STATUS,
			filterType: FilterType.enumMulti,
			enumName: ENUMS.ALL_STATUSES,
			filterPrefix: 'status.',
			showOnMobile: true
		}
	],
	fetchUrl: REPORT.LIST.GET_TIME_STAT_LIST,
	exportName: 'timeStatsReports',
	defaultSort: [{ id: 'ID', desc: true }]
};

const TimeStatsReport = () => {
	const { state } = useLocation();

	const defaultFilters = useMemo(() => {
		if (!state?.data || Object.keys(state?.data)?.length === 0) {
			return [];
		}

		return [
			{
				column: TimeStatsReportConfig.columns.find((col) => col.accessor === 'newOrderDate'),
				action: FilterAction.between,
				value: state.data.date
			}
		];
	}, []);

	return (
		<div>
			<HeaderContainerFormHeader>
				<FormHeader
					lightTitle="reports"
					boldTitle="timeStatsReports"
					icon={faArrowLeft}
					backUrl={mergePaths(PATHS.REPORTS, PATHS.ORDER_REPORTS)}
				/>
			</HeaderContainerFormHeader>

			<DynamicList {...TimeStatsReportConfig} defaultFilters={defaultFilters} />
		</div>
	);
};

export default TimeStatsReport;
